/**
 * Opening Stock / Godown Import — Stores module tab.
 *
 * Manages the Central Godown (Head Office store) independently of
 * project BOQ.  Stock lives in db.opening_stock.
 *
 * Upload wizard (3-step):
 *   Step 1 — Drop / select an Excel (.xlsx) or CSV
 *   Step 2 — Preview parsed rows, matched vs new items, optional qty edit
 *   Step 3 — Confirm → commit → result summary
 *
 * After import, the tab shows the live godown stock table with:
 *   • Inline quantity edit
 *   • Category / search filter
 *   • Export to CSV
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Upload, X, CheckCircle2, AlertTriangle, Loader2, Plus, Search,
  Download, Pencil, Trash2, ChevronLeft, ChevronRight,
  Package, UploadCloud, FileSpreadsheet, RefreshCw,
} from 'lucide-react';
import { apiGet, apiPost, apiUpload, apiPatch, apiDel } from '../../lib/api';

export function OpeningStockTab() {
  const [view, setView] = useState('list');   // 'list' | 'wizard' | 'transfer'
  const [stocks, setStocks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);  // item IDs for transfer
  const [summary, setSummary] = useState({ total_qty: 0, total_value: 0 });
  // B.2: project site layers view
  const [locationView, setLocationView] = useState('godown');   // 'godown' | 'project'
  const [listProjects, setListProjects] = useState([]);
  const [listProjectId, setListProjectId] = useState('');
  const [projectLayers, setProjectLayers] = useState([]);
  const [projectLayersSummary, setProjectLayersSummary] = useState({ total_qty: 0, total_value: 0 });
  const [projectLayersBatches, setProjectLayersBatches] = useState({});
  const [layersLoading, setLayersLoading] = useState(false);
  const LIMIT = 100;

  const loadStocks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search) params.q = search;
      if (catFilter) params.category = catFilter;
      const d = await apiGet('/stores/opening-stock', params);
      setStocks(d.items || []);
      setTotal(d.total || 0);
      setSummary(d.summary || { total_qty: 0, total_value: 0 });
      // Collect unique categories
      if (!catFilter && !search) {
        const cats = [...new Set((d.items || []).map(i => i.category).filter(Boolean))].sort();
        setCategories(cats);
      }
    } catch { toast.error('Failed to load stock'); }
    finally { setLoading(false); }
  }, [page, search, catFilter]);

  // Load stocks on mount and whenever page/search/catFilter changes
  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  // B.2: load project list + layers when locationView changes
  useEffect(() => {
    if (locationView === 'project' && listProjects.length === 0) {
      apiGet('/projects').then(r => setListProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
    }
  }, [locationView, listProjects.length]);

  const loadProjectLayers = useCallback(async () => {
    if (!listProjectId) return;
    setLayersLoading(true);
    try {
      const d = await apiGet('/stores/opening-stock/layers', { project_id: listProjectId });
      setProjectLayers(d.layers || []);
      setProjectLayersSummary(d.summary || { total_qty: 0, total_value: 0 });
      setProjectLayersBatches(d.batches || {});
    } catch { toast.error('Failed to load project layers'); }
    finally { setLayersLoading(false); }
  }, [listProjectId]);

  useEffect(() => { if (listProjectId) loadProjectLayers(); }, [loadProjectLayers]);

  const exportCSV = () => {
    const header = ['Item Code', 'Name', 'Category', 'UOM', 'Qty', 'Rate (₹)', 'Value (₹)', 'Location'];
    const rows = stocks.map(s => [s.item_code, s.material_name, s.category, s.uom, s.qty, s.rate ?? 0, s.value ?? 0, s.location]);
    const csv = [header, ...rows].map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `Central_Godown_Stock_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (view === 'wizard') {
    return <UploadWizard onDone={() => { setView('list'); loadStocks(); }} />;
  }
  if (view === 'transfer') {
    return <TransferWizard stocks={stocks.filter(s => selected.includes(s.id) || selected.length === 0)}
      onDone={() => { setView('list'); setSelected([]); loadStocks(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="opening-stock-tab">
      {/* Location toggle: Godown vs Project Site */}
      <div className="flex gap-2 border-b border-slate-200 pb-3">
        <button onClick={() => setLocationView('godown')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${locationView === 'godown' ? 'bg-[#3b4872] text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'}`}
          data-testid="os-view-godown">
          Central Godown
        </button>
        <button onClick={() => setLocationView('project')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${locationView === 'project' ? 'bg-[#3b4872] text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'}`}
          data-testid="os-view-project">
          Project Site
        </button>
      </div>

      {locationView === 'project' && (
        <ProjectLayersView
          projects={listProjects}
          projectId={listProjectId}
          onProjectChange={setListProjectId}
          layers={projectLayers}
          summary={projectLayersSummary}
          batches={projectLayersBatches}
          loading={layersLoading}
          onRefresh={loadProjectLayers}
          onImport={() => setView('wizard')}
        />
      )}

      {locationView === 'godown' && (<>
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold text-base text-slate-800">Central Godown — Opening Stock</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {total.toLocaleString()} item{total !== 1 ? 's' : ''}
            {summary.total_value > 0 && (
              <span className="ml-2 text-slate-600 font-semibold">
                · Total value ₹{Number(summary.total_value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} disabled={stocks.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold border border-slate-300 text-slate-600 rounded-sm hover:bg-slate-50 disabled:opacity-40"
            data-testid="os-export-btn">
            <Download size={13} /> Export CSV
          </button>
          {stocks.length > 0 && (
            <button onClick={() => setView('transfer')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-[#3b4872]/5"
              data-testid="os-transfer-btn">
              <Package size={13} /> Transfer to Project
            </button>
          )}
          <button onClick={() => setView('wizard')}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658]"
            data-testid="os-import-btn">
            <Upload size={13} /> Import from Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search item code or name…"
            className="pl-8 pr-3 py-2 border border-slate-300 rounded-sm text-xs w-56 focus:outline-none focus:border-[#3b4872]"
            data-testid="os-search" />
        </div>
        <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}
          className="px-2 py-2 border border-slate-300 rounded-sm text-xs bg-white min-w-[160px]"
          data-testid="os-cat-filter">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={loadStocks} className="p-2 border border-slate-300 rounded-sm text-slate-500 hover:bg-slate-50">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={18} />Loading…
        </div>
      ) : stocks.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-16 text-center" data-testid="os-empty">
          <Package size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500 font-semibold">No stock records yet</div>
          <div className="text-xs text-slate-400 mt-1 mb-4">Upload the Central Godown Excel to populate this register</div>
          <button onClick={() => setView('wizard')}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3b4872] text-white text-xs font-display font-bold rounded-sm hover:bg-[#2c3658]">
            <Upload size={13} /> Import from Excel
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full dense-table text-xs" data-testid="os-table">
              <thead>
                <tr>
                  <th className="text-left">Item Code</th>
                  <th className="text-left">Name</th>
                  <th>Category</th>
                  <th className="text-center">UOM</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Rate (₹)</th>
                  <th className="text-right">Value (₹)</th>
                  <th>Location</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {stocks.map(s => (
                  <StockRow key={s.id} stock={s} onChanged={loadStocks} />
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {total > LIMIT && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Showing {((page-1)*LIMIT)+1}–{Math.min(page*LIMIT, total)} of {total}</span>
              <div className="flex gap-1">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="px-2 py-1 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50">
                  <ChevronLeft size={13} />
                </button>
                <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}
                  className="px-2 py-1 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50">
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </> )}
    </div>
  );
}

function ProjectLayersView({ projects, projectId, onProjectChange, layers, summary, batches, loading, onRefresh, onImport }) {
  const fmtINR = (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

  const deleteLayer = async (layerId) => {
    if (!window.confirm('Delete this opening stock layer? This cannot be undone.')) return;
    try {
      await apiDel(`/stores/opening-stock/layer/${layerId}`);
      toast.success('Layer deleted');
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete layer'); }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm(`Delete all layers in batch ${batchId}? This cannot be undone.`)) return;
    try {
      await apiDel(`/stores/opening-stock/batch/${batchId}`);
      toast.success('Batch deleted');
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete batch'); }
  };

  return (
    <div className="space-y-3" data-testid="os-project-layers">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={projectId} onChange={e => onProjectChange(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-2 text-xs bg-white min-w-[240px]"
          data-testid="os-project-layers-project">
          <option value="">— Select project site —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={onRefresh} className="p-2 border border-slate-300 rounded-sm text-slate-500 hover:bg-slate-50"><RefreshCw size={13} /></button>
        <button onClick={onImport}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658]"
          data-testid="os-import-site-btn">
          <Upload size={13} /> Import site stock
        </button>
      </div>

      {!projectId && <div className="text-sm text-slate-400 py-4">Select a project to view its opening stock.</div>}
      {projectId && loading && <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="animate-spin" size={14} />Loading…</div>}
      {projectId && !loading && layers.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-400 text-sm">No opening stock layers for this project. Use "Import site stock" to load existing stock.</div>
      )}
      {projectId && !loading && layers.length > 0 && (
        <>
          <div className="text-xs text-slate-600 font-semibold">
            {layers.length} layer{layers.length !== 1 ? 's' : ''} · Total value ₹{fmtINR(summary.total_value)}
          </div>
          {Object.entries(batches).map(([bid, b]) => (
            <div key={bid} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-slate-200">
                <div className="text-xs font-semibold text-slate-700">Batch {bid} · {b.as_on_date} · {b.count} items · ₹{fmtINR(b.total_value)}</div>
                <button onClick={() => deleteBatch(bid)}
                  className="text-[11px] text-rose-500 hover:text-rose-700 font-semibold"
                  data-testid={`os-delete-batch-${bid}`}>
                  <Trash2 size={12} className="inline mr-1" />Delete whole batch
                </button>
              </div>
              <table className="w-full dense-table text-xs">
                <thead><tr>
                  <th className="text-left">Item Code</th><th className="text-left">Name</th>
                  <th className="text-center">UOM</th><th className="text-right">Orig Qty</th>
                  <th className="text-right">Remaining</th><th className="text-right">Rate (₹)</th>
                  <th className="text-right">Value (₹)</th><th></th>
                </tr></thead>
                <tbody>
                  {layers.filter(l => (l.import_batch_id || 'unknown') === bid).map(l => (
                    <tr key={l.id} className="hover:bg-slate-50" data-testid={`os-layer-${l.id}`}>
                      <td className="font-mono text-[11px] text-slate-500">{l.item_code}</td>
                      <td className="max-w-[200px] truncate">{l.description}</td>
                      <td className="text-center">{l.unit}</td>
                      <td className="text-right tabular-nums">{l.original_qty}</td>
                      <td className={`text-right tabular-nums ${l.remaining_qty < l.original_qty ? 'text-amber-600' : ''}`}>{l.remaining_qty}</td>
                      <td className="text-right tabular-nums">{fmtINR(l.rate)}</td>
                      <td className="text-right tabular-nums font-semibold">{fmtINR(l.value)}</td>
                      <td>
                        {l.consumed_qty === 0 && (
                          <button onClick={() => deleteLayer(l.id)}
                            className="text-slate-300 hover:text-rose-500 p-0.5"
                            data-testid={`os-delete-layer-${l.id}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}


function StockRow({ stock, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(stock.qty ?? 0);
  const [rate, setRate] = useState(stock.rate ?? 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiPatch(`/stores/opening-stock/${stock.id}/qty`, {
        qty: parseFloat(qty) || 0,
        rate: parseFloat(rate) || 0,
      });
      toast.success('Updated');
      setEditing(false);
      onChanged();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Remove "${stock.material_name}" from godown stock?`)) return;
    try {
      await apiDel(`/stores/opening-stock/${stock.id}`);
      toast.success('Removed');
      onChanged();
    } catch { toast.error('Failed to remove'); }
  };

  const fmtINR = (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const value = editing ? ((parseFloat(qty) || 0) * (parseFloat(rate) || 0)) : (stock.value ?? ((stock.qty ?? 0) * (stock.rate ?? 0)));

  return (
    <tr className="hover:bg-slate-50" data-testid={`os-row-${stock.item_code}`}>
      <td className="font-mono text-[11px] text-slate-500">{stock.item_code}</td>
      <td className="max-w-[220px] truncate font-semibold">{stock.material_name}</td>
      <td className="text-center">
        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold">{stock.category}</span>
      </td>
      <td className="text-center">{stock.uom}</td>
      <td className="text-right tabular-nums">
        {editing ? (
          <input type="number" value={qty} onChange={e => setQty(e.target.value)}
            className="w-20 border border-slate-300 rounded px-2 py-1 text-xs text-right focus:outline-none"
            onKeyDown={e => e.key === 'Enter' && save()}
            data-testid={`os-qty-input-${stock.item_code}`} autoFocus />
        ) : (
          <span className={stock.qty === 0 ? 'text-slate-300' : 'font-bold'}>{stock.qty ?? 0}</span>
        )}
      </td>
      <td className="text-right tabular-nums text-slate-600">
        {editing ? (
          <input type="number" value={rate} onChange={e => setRate(e.target.value)}
            className="w-24 border border-slate-300 rounded px-2 py-1 text-xs text-right focus:outline-none"
            data-testid={`os-rate-input-${stock.item_code}`} />
        ) : (
          <span>{fmtINR(stock.rate)}</span>
        )}
      </td>
      <td className="text-right tabular-nums font-semibold text-slate-700" data-testid={`os-value-${stock.item_code}`}>
        {fmtINR(value)}
      </td>
      <td className="text-slate-500 text-[11px]">{stock.location}</td>
      <td>
        <div className="flex items-center gap-1">
          {editing ? (
            saving ? <Loader2 size={12} className="animate-spin" /> : (
              <>
                <button onClick={save} className="text-emerald-600 hover:text-emerald-700"><CheckCircle2 size={14} /></button>
                <button onClick={() => { setEditing(false); setQty(stock.qty ?? 0); setRate(stock.rate ?? 0); }} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
              </>
            )
          ) : (
            <>
              <button onClick={() => setEditing(true)} title="Edit qty / rate"
                className="text-slate-400 hover:text-[#3b4872] p-0.5"
                data-testid={`os-edit-${stock.item_code}`}>
                <Pencil size={12} />
              </button>
              <button onClick={del} title="Remove" className="text-slate-300 hover:text-rose-500 p-0.5"
                data-testid={`os-del-${stock.item_code}`}>
                <Trash2 size={12} />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}


// ── Upload Wizard ────────────────────────────────────────────────────────────
function UploadWizard({ onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);   // { rows, stats }
  const [rows, setRows] = useState([]);            // editable copy with qty/rate
  const [location, setLocation] = useState('Central Godown');
  // Fix #4: location_type — 'godown' (default) or 'project'
  const [locationType, setLocationType] = useState('godown');
  const [locProjectId, setLocProjectId] = useState('');
  const [locProjects, setLocProjects] = useState([]);
  // B.3: As-on date (sent when project site is selected)
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().slice(0, 10));
  // Fix #1: confirm_new checkbox
  const [confirmNew, setConfirmNew] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const dropRef = useRef();

  useEffect(() => {
    // Fix #4: load project list for site stock import
    apiGet('/projects').then(r => setLocProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
  }, []);

  const parseFile = async (f) => {
    if (!f) return;
    setFile(f);
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const d = await apiUpload('/stores/opening-stock/parse', fd);
      setPreview(d);
      // Editable rows with qty and rate from parser
      setRows((d.rows || []).map(r => ({ ...r, qty: r.qty || 0, rate: r.rate || 0 })));
      setConfirmNew(false);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not parse file');
    } finally { setParsing(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const commit = async () => {
    if (!rows.length) return;
    if (locationType === 'project' && !locProjectId) { toast.error('Select a project site'); return; }
    const newCount = (preview?.stats?.new || 0);
    if (newCount > 0 && !confirmNew) { toast.error('Tick the checkbox to create new items, or remove them'); return; }
    setCommitting(true);
    try {
    const payload = {
        rows,
        location: locationType === 'godown' ? location : undefined,
        location_type: locationType,
        project_id: locationType === 'project' ? locProjectId : undefined,
        as_on_date: locationType === 'project' ? asOnDate : undefined,
        confirm_new: confirmNew,
      };
      const res = await apiPost('/stores/opening-stock/commit', payload);
      setResult(res);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setCommitting(false); }
  };

  // ── Step 1: Drop zone ─────────────────────────────────────────────────────
  if (step === 1) return (
    <div className="space-y-4" data-testid="os-wizard-step1">
      <div className="flex items-center gap-2">
        <button onClick={onDone} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
          <ChevronLeft size={13} /> Back to stock list
        </button>
        <span className="text-xs text-slate-300">|</span>
        <span className="text-xs font-semibold text-slate-700">Step 1 of 3 — Upload Excel</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-8 max-w-2xl">
        <div className="flex items-center gap-3 mb-5">
          <FileSpreadsheet size={24} className="text-[#3b4872]" />
          <div>
            <div className="font-display font-bold text-base">Import Inventory from Excel</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Upload your Central Godown Excel (.xlsx, .xls) or CSV.
              The wizard auto-detects columns for Item Code, Item Desc, Unit, and Item Group.
            </div>
          </div>
        </div>

        {/* Drop zone */}
        <div
          ref={dropRef}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-[#3b4872] hover:bg-[#3b4872]/5 transition-colors cursor-pointer"
          onClick={() => document.getElementById('os-file-input').click()}
          data-testid="os-drop-zone"
        >
          {parsing ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={32} className="animate-spin text-[#3b4872]" />
              <div className="text-sm text-slate-600">Parsing file…</div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <UploadCloud size={36} className="text-slate-300" />
              <div className="text-sm font-semibold text-slate-600">Drag & drop your Excel file here</div>
              <div className="text-xs text-slate-400">or click to browse</div>
              <div className="text-xs text-slate-300 mt-1">Supports .xlsx · .xls · .csv</div>
            </div>
          )}
        </div>
        <input id="os-file-input" type="file"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={e => parseFile(e.target.files?.[0])} />

        {/* Column format guide */}
        <div className="mt-5 bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
          <div className="font-semibold mb-1.5">Auto-detected columns (at least 3 required):</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div><span className="font-mono bg-white border border-slate-200 px-1 rounded">Item Code</span> → material_id</div>
            <div><span className="font-mono bg-white border border-slate-200 px-1 rounded">Item Desc</span> → name</div>
            <div><span className="font-mono bg-white border border-slate-200 px-1 rounded">Unit</span> → UOM</div>
            <div><span className="font-mono bg-white border border-slate-200 px-1 rounded">Item Group</span> → category</div>
            <div><span className="font-mono bg-white border border-slate-200 px-1 rounded">Qty</span> → opening qty (optional)</div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Step 2: Preview & qty entry ──────────────────────────────────────────
  if (step === 2 && preview) {
    const stats = preview.stats || {};
    const newCount = stats.new || 0;
    const canImport = newCount === 0 || confirmNew;
    const previewTotal = rows.reduce((s, r) => s + ((parseFloat(r.qty) || 0) * (parseFloat(r.rate) || 0)), 0);
    return (
      <div className="space-y-4" data-testid="os-wizard-step2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => setStep(1)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
              <ChevronLeft size={13} /> Back
            </button>
            <span className="text-xs font-semibold text-slate-700">Step 2 of 3 — Review & set quantities</span>
          </div>
          <button onClick={commit} disabled={committing || rows.length === 0 || !canImport}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50 hover:bg-[#2c3658]"
            data-testid="os-commit-btn">
            {committing ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {committing ? 'Importing…' : `Import ${rows.length} items`}
          </button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total rows" value={stats.total} color="slate" />
          <StatCard label="New (to be created)" value={newCount} color="blue" />
          <StatCard label="Matched existing" value={stats.matched} color="emerald" />
        </div>

        {/* Fix #1: confirm_new notice when new items exist */}
        {newCount > 0 && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-amber-800">{newCount} item{newCount !== 1 ? 's' : ''} not in Material Master — they will be created.</div>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={confirmNew} onChange={e => setConfirmNew(e.target.checked)}
                  className="h-3.5 w-3.5" data-testid="os-confirm-new-checkbox" />
                <span className="text-xs text-amber-900">Create {newCount} new item{newCount !== 1 ? 's' : ''} in Material Master</span>
              </label>
              {!confirmNew && (
                <div className="text-[11px] text-amber-700 mt-1">Tick the checkbox to enable Import, or remove the new rows from the sheet.</div>
              )}
            </div>
          </div>
        )}

        {/* Fix #4: Location type selector */}
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 space-y-2">
          <div className="flex items-center gap-3">
            <div className="text-xs font-semibold text-slate-600 w-28 shrink-0">Stock location</div>
            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" value="godown" checked={locationType === 'godown'}
                  onChange={() => setLocationType('godown')} data-testid="os-loc-godown" />
                Central Godown
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" value="project" checked={locationType === 'project'}
                  onChange={() => setLocationType('project')} data-testid="os-loc-project" />
                Project Site
              </label>
            </div>
          </div>
          {locationType === 'godown' ? (
            <div className="flex items-center gap-3">
              <div className="text-xs text-slate-500 w-28 shrink-0">Location name</div>
              <input value={location} onChange={e => setLocation(e.target.value)}
                className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872]"
                placeholder="Central Godown" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="text-xs text-slate-500 w-28 shrink-0">Project *</div>
              <select value={locProjectId} onChange={e => setLocProjectId(e.target.value)}
                className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872]"
                data-testid="os-loc-project-select">
                <option value="">— Select project site —</option>
                {locProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          {locationType === 'project' && (
            <div className="flex items-center gap-3">
              <div className="text-xs text-slate-500 w-28 shrink-0">As on date *</div>
              <input type="date" value={asOnDate} onChange={e => setAsOnDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872]"
                data-testid="os-as-on-date" />
            </div>
          )}
        </div>

        {/* Preview total value */}
          <div className="text-right text-xs text-slate-600 font-semibold" data-testid="os-preview-total-value">
            Total stock value: ₹{previewTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>

        {/* Preview table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full dense-table text-xs">
            <thead className="sticky top-0 bg-white border-b border-slate-200 z-10">
              <tr>
                <th className="text-left px-3 py-2">Item Code</th>
                <th className="text-left">Name</th>
                <th>Category</th>
                <th className="text-center">UOM</th>
                <th className="text-center w-24">Qty</th>
                <th className="text-center w-28">Rate (₹)</th>
                <th className="text-right w-24">Value (₹)</th>
                <th className="text-center w-20">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rowVal = (parseFloat(r.qty) || 0) * (parseFloat(r.rate) || 0);
                return (
                  <tr key={i} className={r.matched ? 'bg-emerald-50/40' : ''} data-testid={`preview-row-${i}`}>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{r.item_code}</td>
                    <td className="py-1.5 max-w-[200px] truncate font-semibold">{r.name}</td>
                    <td className="py-1.5 text-center text-slate-500">{r.category}</td>
                    <td className="py-1.5 text-center">{r.uom}</td>
                    <td className="py-1.5 text-center">
                      <input type="number" min="0" step="any"
                        value={r.qty}
                        onChange={e => setRows(rws => rws.map((rw, j) => j === i ? { ...rw, qty: e.target.value } : rw))}
                        className="w-20 border border-slate-300 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-[#3b4872]"
                        data-testid={`preview-qty-${i}`} />
                    </td>
                    <td className="py-1.5 text-center">
                      <input type="number" min="0" step="any"
                        value={r.rate}
                        onChange={e => setRows(rws => rws.map((rw, j) => j === i ? { ...rw, rate: e.target.value } : rw))}
                        className="w-24 border border-slate-300 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-[#3b4872]"
                        data-testid={`preview-rate-${i}`} />
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-slate-600">
                      {rowVal > 0 ? rowVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                    </td>
                    <td className="py-1.5 text-center">
                      {r.value_without_qty && (
                        <span className="inline-flex items-center gap-0.5 text-rose-600 text-[10px] font-bold"><AlertTriangle size={10} />value/0 qty</span>
                      )}
                      {!r.value_without_qty && (r.matched
                        ? <span className="inline-flex items-center gap-0.5 text-emerald-600 text-[10px] font-bold"><CheckCircle2 size={11} />matched</span>
                        : <span className="inline-flex items-center gap-0.5 text-blue-600 text-[10px] font-bold"><Plus size={10} />new</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-400">
          You can edit quantities and rates above. Rates can also be updated later via inline edit on the stock list.
        </div>
      </div>
    );
  }

  // ── Step 3: Result ────────────────────────────────────────────────────────
  if (step === 3 && result) {
    const skippedRows = (result.rows || []).filter(r => r.status === 'needs_confirmation' || r.status === 'zero_qty');

    const downloadSkipped = () => {
      const header = ['Item Code', 'Name', 'Reason'];
      const csvRows = skippedRows.map(r => [r.item_code, r.name, r.status === 'zero_qty' ? 'Zero quantity' : 'Not in Material Master']);
      const csv = [header, ...csvRows].map(row => row.map(c => `"${c ?? ''}"`).join(',')).join('\n');
      const a = document.createElement('a');
      a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      a.download = `skipped_rows_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    };
    return (
      <div className="space-y-4 max-w-md" data-testid="os-wizard-step3">
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
          <div className="font-display font-bold text-lg text-slate-800 mb-1">Import complete</div>
          <div className="text-sm text-slate-500 mb-5">
            {locationType === 'project' ? 'Site stock layers created.' : 'Central Godown stock has been updated.'}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <StatCard label="New items" value={result.inserted} color="blue" />
            <StatCard label="Updated" value={result.updated} color="emerald" />
            <StatCard label="Skipped" value={result.skipped} color="slate" />
          </div>
          {skippedRows.length > 0 && (
            <div className="mb-5 text-left">
              <div className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                <AlertTriangle size={13} className="text-amber-500" />
                {skippedRows.length} row{skippedRows.length !== 1 ? 's' : ''} not imported:
              </div>
              <div className="border border-amber-200 rounded text-xs max-h-40 overflow-y-auto">
                {skippedRows.map((r, i) => (
                  <div key={i} className="px-3 py-1.5 border-b border-amber-100 last:border-0 flex justify-between">
                    <span className="font-mono text-slate-500">{r.item_code}</span>
                    <span className="text-slate-700">{r.name}</span>
                    <span className="text-[10px] text-slate-400">{r.status === 'zero_qty' ? 'zero qty' : 'new item'}</span>
                  </div>
                ))}
              </div>
              <button onClick={downloadSkipped}
                className="mt-2 flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 font-semibold"
                data-testid="os-download-skipped-btn">
                <Download size={12} /> Download skipped rows CSV
              </button>
            </div>
          )}
          <button onClick={onDone}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#3b4872] text-white text-sm font-display font-bold rounded-sm hover:bg-[#2c3658]"
            data-testid="os-done-btn">
            {locationType === 'project' ? 'View Site Stock' : 'View Godown Stock'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

function StatCard({ label, value, color }) {
  const colors = {
    slate:   'bg-slate-50 border-slate-200 text-slate-700',
    blue:    'bg-blue-50 border-blue-200 text-blue-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div className={`border rounded-sm p-3 text-center ${colors[color]}`}>
      <div className="text-xl font-display font-bold">{value ?? 0}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider mt-0.5 opacity-70">{label}</div>
    </div>
  );
}


// ── Transfer Wizard ──────────────────────────────────────────────────────────
function TransferWizard({ onDone }) {
  const [projects, setProjects] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [lines, setLines] = useState([]);    // [{stock_id, material_name, item_code, uom, available_qty, qty}]
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiGet('/projects').then(r => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
    apiGet('/stores/opening-stock', { limit: 500 })
      .then(d => setStocks((d.items || []).filter(s => s.qty > 0)))
      .catch(() => {});
  }, []);

  const addLine = (stock) => {
    if (lines.find(l => l.stock_id === stock.id)) return;
    setLines(ls => [...ls, {
      stock_id: stock.id,
      material_name: stock.material_name,
      item_code: stock.item_code,
      uom: stock.uom,
      available_qty: stock.qty,
      qty: '',
    }]);
  };

  const removeLine = (stock_id) => setLines(ls => ls.filter(l => l.stock_id !== stock_id));
  const updQty = (stock_id, v) => setLines(ls => ls.map(l => l.stock_id === stock_id ? { ...l, qty: v } : l));

  const commit = async () => {
    if (!projectId) { toast.error('Select a destination project'); return; }
    const items = lines.filter(l => parseFloat(l.qty) > 0).map(l => ({
      stock_id: l.stock_id,
      qty: parseFloat(l.qty),
    }));
    if (!items.length) { toast.error('Enter transfer quantities for at least one item'); return; }
    setCommitting(true);
    try {
      const r = await apiPost('/stores/opening-stock/transfer', { project_id: projectId, items, remarks });
      setResult(r);
    } catch (e) {
      const d = e?.response?.data;
      if (d?.errors) toast.error(d.errors.join('\n'));
      else toast.error(d?.message || e?.message || 'Transfer failed');
    } finally { setCommitting(false); }
  };

  if (result) return (
    <div className="max-w-md space-y-4" data-testid="transfer-result">
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
        <div className="font-display font-bold text-lg text-slate-800 mb-1">Dispatched — in transit</div>
        <div className="text-xs font-mono text-slate-500 mb-4">{result.transfer_no}</div>
        <div className="text-sm text-slate-600 mb-5" data-testid="transfer-result-note">
          {result.transfer?.items?.length} item(s) dispatched to <strong>{result.transfer?.to?.name}</strong>.
          Stock lands at the site once the site store confirms receipt in <b>Stores → Transfers</b>.
        </div>
        <button onClick={onDone}
          className="px-6 py-2.5 bg-[#3b4872] text-white text-sm font-display font-bold rounded-sm hover:bg-[#2c3658]"
          data-testid="transfer-done-btn">
          Done
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 max-w-3xl" data-testid="transfer-wizard">
      <div className="flex items-center gap-2">
        <button onClick={onDone} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
          <ChevronLeft size={13} /> Back
        </button>
        <span className="text-xs font-semibold text-slate-700">Transfer Godown Items to Project</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
        {/* Destination project */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Destination Project *</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
            data-testid="transfer-project-select">
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>

        {/* Item picker */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Add items from Godown</label>
          <select onChange={e => { const s = stocks.find(x => x.id === e.target.value); if (s) addLine(s); e.target.value = ''; }}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] bg-white"
            data-testid="transfer-item-picker">
            <option value="">+ Click to add a godown item…</option>
            {stocks.filter(s => !lines.find(l => l.stock_id === s.id)).map(s => (
              <option key={s.id} value={s.id}>[{s.item_code}] {s.material_name} ({s.qty} {s.uom} avail.)</option>
            ))}
          </select>
        </div>

        {/* Transfer lines */}
        {lines.length > 0 && (
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left">Item Code</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-center">Available</th>
                  <th className="px-3 py-2 text-center w-28">Transfer Qty *</th>
                  <th className="px-3 py-2 text-center">UOM</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.stock_id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{l.item_code}</td>
                    <td className="px-3 py-2 max-w-[200px] truncate">{l.material_name}</td>
                    <td className="px-3 py-2 text-center text-slate-500">{l.available_qty}</td>
                    <td className="px-3 py-2">
                      <input type="number" min="0" max={l.available_qty} step="any"
                        value={l.qty}
                        onChange={e => updQty(l.stock_id, e.target.value)}
                        className={`w-full border rounded px-2 py-1 text-xs text-right focus:outline-none ${
                          parseFloat(l.qty) > l.available_qty ? 'border-rose-400 bg-rose-50' : 'border-slate-300 focus:border-[#3b4872]'
                        }`}
                        data-testid={`transfer-qty-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-center">{l.uom}</td>
                    <td className="px-1 py-2 text-center">
                      <button onClick={() => removeLine(l.stock_id)} className="text-slate-300 hover:text-rose-500">
                        <X size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Remarks */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks</label>
          <input value={remarks} onChange={e => setRemarks(e.target.value)}
            placeholder="e.g. Site mobilisation materials"
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" />
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={commit} disabled={committing || !projectId || lines.length === 0}
            className="flex-1 flex items-center justify-center gap-2 bg-[#3b4872] text-white py-2.5 rounded-sm font-display font-bold text-sm disabled:opacity-50 hover:bg-[#2c3658]"
            data-testid="transfer-commit-btn">
            {committing ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
            {committing ? 'Transferring…' : `Transfer ${lines.filter(l => parseFloat(l.qty) > 0).length} item(s)`}
          </button>
          <button onClick={onDone} className="px-4 py-2.5 border border-slate-300 rounded-sm text-sm text-slate-600">Cancel</button>
        </div>
      </div>
    </div>
  );
}
